import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { LogoMark } from "@/components/Logo";
import Landing from "@/pages/Landing";
import Contact from "@/pages/Contact";
import Auth from "@/pages/Auth";
import Quiz from "@/pages/Quiz";
import Discover from "@/pages/Discover";
import Matches from "@/pages/Matches";
import Chat from "@/pages/Chat";
import Profile from "@/pages/Profile";
import Avis from "@/pages/Avis";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Admin from "@/pages/Admin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Gift, { GiftSuccess } from "@/pages/Gift";
import Legal from "@/pages/Legal";
import SpeedDating, { SpeedSuccess } from "@/pages/SpeedDating";
import MonCompte from "@/pages/MonCompte";

const Loader = () => (
  <div className="flex min-h-screen items-center justify-center bg-cream" data-testid="app-loader">
    <div className="animate-pulse">
      <LogoMark size={64} />
    </div>
  </div>
);

const Protected = () => {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/auth" replace />;
  return (
    <div className="grain min-h-screen bg-cream">
      <Navbar />
      <Outlet />
    </div>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route path="/quiz-brouillon" element={<Quiz draft />} />
          <Route path="/offrir" element={<Gift />} />
          <Route path="/offrir/succes" element={<GiftSuccess />} />
          <Route path="/speed-dating" element={<SpeedDating />} />
          <Route path="/speed-dating/succes" element={<SpeedSuccess />} />
          <Route path="/cgv" element={<Legal kind="cgv" />} />
          <Route path="/mentions-legales" element={<Legal kind="mentions-legales" />} />
          <Route path="/confidentialite" element={<Legal kind="confidentialite" />} />
          <Route path="/pua" element={<Legal kind="pua" />} />
          <Route path="/protocole-forces-ordre" element={<Legal kind="protocole" />} />
          <Route element={<Protected />}>
            <Route path="/mon-compte" element={<MonCompte />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/matches" element={<Matches />} />
            <Route path="/chat/:matchId" element={<Chat />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/avis" element={<Avis />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
